import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { Stack } from '@raptormaps/layout';
import { Text } from '@raptormaps/text';

import {
  getSolarFarmsByNameQuery,
  SolarFarmSearchResponse,
} from '../../api/services/solar-farm';
import { SolarFarm } from '../../api/schemas/SolarFarmSchema';
import { useSearchParams } from '../../hooks/useSearchParams';

interface SolarFarmOption {
  id: number;
  uuid: string;
  name: string;
}

const SolarFarmTypeaheadOption = ({
  solarFarm,
}: {
  solarFarm: SolarFarmOption;
}) => (
  <Stack gutter="xxs" style={{ padding: '6px' }}>
    <Text as="span" variant="paragraph_large">
      {solarFarm.name}
    </Text>
    <Text as="span" variant="paragraph_small" color="neutral_600">
      {solarFarm.id}
    </Text>
  </Stack>
);

interface SolarFarmTypeaheadProps {
  selectedFarm: SolarFarm | undefined;
  isLoadingSelectedFarm: boolean;
}

export const SolarFarmTypeahead = ({
  selectedFarm,
  isLoadingSelectedFarm,
}: SolarFarmTypeaheadProps) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const searchParams = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string>(
    selectedFarm?.name ?? '',
  );

  const farmNameSearchText =
    searchTerm.trim().length > 0 ? searchTerm : selectedFarm?.name ?? null;

  const {
    data: solarFarmSearchResults,
    isFetching,
    isIdle,
  } = useQuery({
    ...getSolarFarmsByNameQuery(farmNameSearchText ?? ''),
    select: data => data.solar_farms,
    enabled: farmNameSearchText !== null,
  });

  const handleSelectFarm = (selectedFarms: Array<SolarFarmOption>) => {
    const newSelectedFarm = selectedFarms.at(0);
    if (newSelectedFarm) {
      searchParams.set('farm', newSelectedFarm.uuid);

      const selectedFarmSearchResult = solarFarmSearchResults?.find(
        farm => farm.uuid === newSelectedFarm.uuid,
      );
      if (selectedFarmSearchResult) {
        // prefills cache for the selected farm so that this component doesn't need to refetch
        queryClient.setQueryData<SolarFarmSearchResponse>(
          getSolarFarmsByNameQuery(newSelectedFarm.name).queryKey!,
          {
            solar_farms: [selectedFarmSearchResult],
          },
        );
      }

      history.push({ search: searchParams.toString() });
    }
  };

  const selectedFarmOption: SolarFarmOption | undefined = useMemo(() => {
    if (selectedFarm && selectedFarm.name === farmNameSearchText) {
      return {
        id: selectedFarm.id,
        name: selectedFarm.name,
        uuid: selectedFarm.uuid,
      };
    }
  }, [farmNameSearchText, selectedFarm]);

  // a hack for the Typeahead - otherwise results won't appear in the menu
  const [isDoneFetching, setIsDoneFetching] = useState<boolean>(true);
  useEffect(() => {
    setIsDoneFetching(isIdle || !isFetching);
  }, [isIdle, isFetching]);

  return (
    <div style={{ minWidth: 400 }}>
      <AsyncTypeahead
        id="farm-search"
        selected={selectedFarmOption ? [selectedFarmOption] : undefined}
        onChange={handleSelectFarm}
        options={solarFarmSearchResults ?? []}
        onSearch={setSearchTerm}
        disabled={isLoadingSelectedFarm}
        isLoading={!isDoneFetching || isLoadingSelectedFarm}
        labelKey={farm => farm.name}
        minLength={3}
        placeholder={
          isLoadingSelectedFarm
            ? 'Loading selected farm...'
            : 'Type to Search Farms...'
        }
        renderMenuItemChildren={option => (
          <SolarFarmTypeaheadOption solarFarm={option} />
        )}
      />
    </div>
  );
};
